import { tokenManager } from "../../api/tokenManager";
import { getUrlFromBlob } from "../utils";
import { API_URL } from "../../api/apiClient";

interface CachedImage {
  blobUrl: string;
  timestamp: number;
  isError: boolean;
}

class ImageCacheService {
  private cache: Map<string, CachedImage> = new Map();
  private readonly MAX_AGE = 30 * 60 * 1000;
  private readonly ERROR_MAX_AGE = 5 * 60 * 1000;

  private getCacheKey(entityName: string, fieldName: string, entityId: string, fileName: string): string {
    return `${entityName}.${fieldName}/${entityId}/${fileName}`;
  }

  async getImage(
    entityName: string,
    fieldName: string,
    entityId: string,
    fileName: string,
    activityStore?: any
  ): Promise<string | null> {
    const cacheKey = this.getCacheKey(entityName, fieldName, entityId, fileName);

    const cached = this.cache.get(cacheKey);
    if (cached) {
      const maxAge = cached.isError ? this.ERROR_MAX_AGE : this.MAX_AGE;
      const age = Date.now() - cached.timestamp;

      if (age < maxAge) {
        return cached.isError ? null : cached.blobUrl;
      } else {
        if (!cached.isError && cached.blobUrl) {
          URL.revokeObjectURL(cached.blobUrl);
        }
        this.cache.delete(cacheKey);
      }
    }

    return this.fetchImage(entityName, fieldName, entityId, fileName, activityStore);
  }

  private async fetchImage(
    entityName: string,
    fieldName: string,
    entityId: string,
    fileName: string,
    activityStore?: any
  ): Promise<string | null> {
    const cacheKey = this.getCacheKey(entityName, fieldName, entityId, fileName);
    const activityId = activityStore?.startActivity("query", "imageDownload");

    try {
      const token = await tokenManager.getValidAccessToken();
      if (!token) {
        if (activityId) activityStore?.endActivity(activityId);
        throw new Error("No authentication token available");
      }

      const downloadUrl = `/${entityName}.${fieldName}/${entityId}/${fileName}`;
      const fullUrl = `${API_URL}/download${downloadUrl}`;

      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (activityId) activityStore?.endActivity(activityId);
        throw new Error(`Failed to load image: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = getUrlFromBlob(blob);

      this.cache.set(cacheKey, {
        blobUrl,
        timestamp: Date.now(),
        isError: false,
      });

      if (activityId) activityStore?.endActivity(activityId);
      return blobUrl;
    } catch (error) {
      if (activityId) activityStore?.endActivity(activityId);
      console.error("Error fetching image:", error);

      this.cache.set(cacheKey, {
        blobUrl: "",
        timestamp: Date.now(),
        isError: true,
      });

      return null;
    }
  }

  clearCache() {
    this.cache.forEach((cached) => {
      if (!cached.isError && cached.blobUrl) {
        URL.revokeObjectURL(cached.blobUrl);
      }
    });
    this.cache.clear();
  }

  removeFromCache(entityName: string, fieldName: string, entityId: string, fileName: string) {
    const cacheKey = this.getCacheKey(entityName, fieldName, entityId, fileName);
    const cached = this.cache.get(cacheKey);
    if (cached && !cached.isError && cached.blobUrl) {
      URL.revokeObjectURL(cached.blobUrl);
    }
    this.cache.delete(cacheKey);
  }
}

export const imageCache = new ImageCacheService();
